/**
 * 手机端专属的两件小事：网络变化要说话、来电之后要自己缓过来。
 *
 * 都只在手机布局下挂上；桌面有常驻的信号格，也没有来电这回事。
 */

import { useEffect, useRef } from 'react'

import { connToastFor, netToastFor } from './netToast'
import { useRoomStore } from '../stores/roomStore'

/** 网络质量跨过「差」这条线、或 WiFi ⇄ 流量切换时弹一句。 */
export function useNetToasts(enabled: boolean): void {
  const level = useRoomStore((s) => s.net.level)
  const setToast = useRoomStore((s) => s.setToast)
  const prev = useRef(level)

  useEffect(() => {
    if (!enabled) return
    const msg = netToastFor(prev.current, level)
    prev.current = level
    if (msg) setToast(msg)
  }, [enabled, level, setToast])

  useEffect(() => {
    if (!enabled) return
    // navigator.connection 只有 Chromium 系有；Safari 没有就只靠上面的质量判定。
    const conn = (navigator as Navigator & { connection?: EventTarget & { type?: string } }).connection
    if (!conn) return
    let last = conn.type
    const onChange = () => {
      const msg = connToastFor(last, conn.type)
      last = conn.type
      if (msg) setToast(msg)
    }
    conn.addEventListener('change', onChange)
    return () => conn.removeEventListener('change', onChange)
  }, [enabled, setToast])
}

/**
 * 来电 / 切后台回来后，把被系统掐断的本地轨换回来。
 *
 * 手机上电话打进来，系统会直接把麦克风和摄像头从网页手里收走：
 * track 的 readyState 变成 'ended'，而 WebRTC 连接还活着 —— 于是
 * 界面上麦克风显示开着，对面却什么都听不到，而且没有任何报错。
 * 只能在页面回到前台时自己检查一遍，断了的重新拿一路换上去。
 */
export function useCallRecovery(enabled: boolean): void {
  const media = useRoomStore((s) => s.media)
  const setToast = useRoomStore((s) => s.setToast)

  useEffect(() => {
    if (!enabled || !media) return
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      void media.recoverLocalTracks().then((r) => {
        if (r.audio || r.video) setToast('已恢复' + [r.audio && '麦克风', r.video && '摄像头'].filter(Boolean).join('和'))
      })
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [enabled, media, setToast])
}
